// AudioManager central do site — fluxo narrativo (Primeira Vibração) + política de autoplay.
//
// Landing = "o Nada antes da Primeira Vibração" (silêncio). O clique do gate é o gesto que
// desbloqueia o áudio: click → hero_intro → som_loop_perfeito (música em loop contínuo, global,
// que se mantém em todas as páginas). Na PRIMEIRA vez que a música surge, entra com um fade-in
// crescente subtil. Controlos: volume, silenciar tudo, silenciar só a música (mantendo cliques).

import { useSyncExternalStore } from "react";

// A música é um loop perfeito em .ogg (fallback .mp3 para browsers sem Ogg, ex. Safari).
const canOgg = (() => {
  try { return /maybe|probably/.test(new Audio().canPlayType("audio/ogg")); } catch { return false; }
})();
const LOOP_SRC = canOgg ? "/audio/som_loop_perfeito.ogg" : "/audio/som_loop_perfeito.mp3";

const FILES = {
  click: "/audio/click.mp3",
  hover: "/audio/hover.mp3",
  voice: "/audio/voice.mp3",
  hero_intro: "/audio/hero_intro.mp3",
  hero_loop: LOOP_SRC,
};
const VOL = { click: 0.65, hover: 0.22, voice: 0.55, hero_intro: 0.75, hero_loop: 0.28 };
const LOOP_FADE_IN = 1400;         // ms — retoma (nav/reload)
const LOOP_FADE_IN_FIRST = 4200;   // ms — primeira aparição: crescendo subtil
const LOOP_FADE_OUT = 1000;        // ms
const LOOP_CROSSFADE = 1.6;        // s — sobreposição no fim/início do loop
const LOOP_SCHEDULE_AHEAD = 8;     // s — agenda antes da costura chegar
const LOOP_SCHEDULE_TICK = 900;    // ms
const LOOP_START_OFFSET = 0.08;    // s — pequena margem contra atrasos no arranque

const MKEY = "som_muted";          // silenciar TUDO
const MMKEY = "som_music_muted";   // silenciar só a música
const VKEY = "som_volume";         // volume-mestre 0..1
const EKEY = "som_entered";        // rompeu a Primeira Vibração NESTA sessão (sessionStorage)
const LIKEY = "som_loop_intro";    // música já foi introduzida nesta sessão (sessionStorage)

function readBool(store, key) { try { return store.getItem(key) === "1"; } catch { return false; } }

let muteAll = readBool(localStorage, MKEY);
let muteMusic = readBool(localStorage, MMKEY);
let volume = 0.8;
try { const v = parseFloat(localStorage.getItem(VKEY)); if (!Number.isNaN(v)) volume = Math.max(0, Math.min(1, v)); } catch { /* ignore */ }

let entered = readBool(sessionStorage, EKEY);
let loopIntroduced = readBool(sessionStorage, LIKEY);
let phase = entered ? "loop" : "landing"; // 'landing' | 'intro' | 'loop'

let loopAudio = null; // fallback HTMLAudio
let introAudio = null;
let fadeRaf = 0;
let fadeTimeout = 0;
let loopCtx = null;
let loopMaster = null;
let loopBuffer = null;
let loopBufferPromise = null;
let loopActive = false;
let loopNextStart = 0;
let loopScheduler = 0;
let loopGeneration = 0;
const loopSources = new Set();

const bases = {};
const subs = new Set();
let snapshot = buildSnapshot();

function buildSnapshot() { return { muteAll, muteMusic, volume, entered, phase }; }
function emit() { snapshot = buildSnapshot(); subs.forEach((fn) => fn()); }

function persist(key, val) { try { localStorage.setItem(key, val); } catch { /* ignore */ } }

const musicOn = () => !muteAll && !muteMusic;
const loopTarget = () => VOL.hero_loop * volume;

function canUseWebAudio() {
  return typeof window !== "undefined" && Boolean(window.AudioContext || window.webkitAudioContext);
}

function ensureLoopContext() {
  if (!canUseWebAudio()) return null;
  if (!loopCtx) {
    const AudioContextImpl = window.AudioContext || window.webkitAudioContext;
    loopCtx = new AudioContextImpl();
    loopMaster = loopCtx.createGain();
    loopMaster.gain.value = 0;
    loopMaster.connect(loopCtx.destination);
  }
  return loopCtx;
}

function loadLoopBuffer(ctx = ensureLoopContext()) {
  if (!ctx) return Promise.reject(new Error("Web Audio indisponível."));
  if (loopBuffer) return Promise.resolve(loopBuffer);
  if (!loopBufferPromise) {
    loopBufferPromise = fetch(FILES.hero_loop, { cache: "force-cache" })
      .then(async (res) => {
        if (!res.ok) throw new Error(`Não foi possível carregar ${FILES.hero_loop}`);
        const bytes = await res.arrayBuffer();
        return ctx.decodeAudioData(bytes);
      })
      .then((buffer) => {
        loopBuffer = buffer;
        return buffer;
      })
      .catch((err) => {
        loopBufferPromise = null;
        throw err;
      });
  }
  return loopBufferPromise;
}

function primeLoop() {
  const ctx = ensureLoopContext();
  if (!ctx) return Promise.resolve(null);
  try {
    const p = ctx.resume();
    if (p && p.catch) p.catch(() => {});
  } catch { /* ignore */ }
  return loadLoopBuffer(ctx).catch(() => null);
}

function loopFadeSeconds() {
  if (!loopBuffer) return LOOP_CROSSFADE;
  return Math.min(LOOP_CROSSFADE, Math.max(0.25, loopBuffer.duration / 3));
}

function setWebLoopVolume(target, ms = 0) {
  if (!loopCtx || !loopMaster) return;
  const now = loopCtx.currentTime;
  const gain = loopMaster.gain;
  const current = gain.value;
  gain.cancelScheduledValues(now);
  gain.setValueAtTime(current, now);
  if (ms > 0) gain.linearRampToValueAtTime(Math.max(0, Math.min(1, target)), now + (ms / 1000));
  else gain.setValueAtTime(Math.max(0, Math.min(1, target)), now);
}

function scheduleLoopSource(startAt, generation) {
  if (!loopCtx || !loopMaster || !loopBuffer || !loopActive || generation !== loopGeneration) return;
  const duration = loopBuffer.duration;
  const fade = loopFadeSeconds();
  const source = loopCtx.createBufferSource();
  const gain = loopCtx.createGain();

  source.buffer = loopBuffer;
  gain.gain.setValueAtTime(0, startAt);
  gain.gain.linearRampToValueAtTime(1, startAt + fade);
  gain.gain.setValueAtTime(1, Math.max(startAt + fade, startAt + duration - fade));
  gain.gain.linearRampToValueAtTime(0, startAt + duration);

  source.connect(gain);
  gain.connect(loopMaster);
  loopSources.add(source);
  source.onended = () => {
    loopSources.delete(source);
    try { source.disconnect(); gain.disconnect(); } catch { /* ignore */ }
  };
  source.start(startAt);
  source.stop(startAt + duration + 0.05);
}

function clearLoopScheduler() {
  if (loopScheduler) {
    clearTimeout(loopScheduler);
    loopScheduler = 0;
  }
}

function scheduleLoopAhead(generation) {
  if (!loopActive || generation !== loopGeneration || !loopCtx || !loopBuffer) return;
  const fade = loopFadeSeconds();
  const cycle = Math.max(0.25, loopBuffer.duration - fade);
  const ahead = loopCtx.currentTime + LOOP_SCHEDULE_AHEAD;
  while (loopNextStart < ahead) {
    scheduleLoopSource(loopNextStart, generation);
    loopNextStart += cycle;
  }
  loopScheduler = setTimeout(() => scheduleLoopAhead(generation), LOOP_SCHEDULE_TICK);
}

function stopWebLoop(immediate = false) {
  if (!loopActive && !loopSources.size) return;
  const generation = ++loopGeneration;
  loopActive = false;
  clearLoopScheduler();

  const stopSources = () => {
    for (const source of Array.from(loopSources)) {
      try { source.stop(0); } catch { /* ignore */ }
      try { source.disconnect(); } catch { /* ignore */ }
    }
    loopSources.clear();
  };

  if (immediate || !loopCtx || !loopMaster) {
    setWebLoopVolume(0, 0);
    stopSources();
    return;
  }

  setWebLoopVolume(0, LOOP_FADE_OUT);
  setTimeout(() => {
    if (generation !== loopGeneration) return;
    stopSources();
  }, LOOP_FADE_OUT + 120);
}

async function startWebLoop(ms) {
  const ctx = ensureLoopContext();
  if (!ctx) throw new Error("Web Audio indisponível.");
  const generation = ++loopGeneration;
  loopActive = true;

  try {
    await ctx.resume();
    await loadLoopBuffer(ctx);
    if (!loopActive || generation !== loopGeneration) return;
    if (loopAudio) { try { loopAudio.pause(); loopAudio.currentTime = 0; } catch { /* ignore */ } }
    clearLoopScheduler();
    loopNextStart = ctx.currentTime + LOOP_START_OFFSET;
    setWebLoopVolume(0, 0);
    scheduleLoopAhead(generation);
    setWebLoopVolume(loopTarget(), ms);
  } catch (err) {
    if (generation === loopGeneration) {
      loopActive = false;
      clearLoopScheduler();
    }
    throw err;
  }
}

function startHtmlLoop(ms) {
  stopWebLoop(true);
  if (!loopAudio) {
    loopAudio = new Audio(FILES.hero_loop);
    loopAudio.loop = true;
    loopAudio.preload = "auto";
  }
  if (loopAudio.paused || loopAudio.volume < 0.005) {
    loopAudio.volume = 0;
    const p = loopAudio.play();
    if (p && p.catch) p.catch(() => {});
    fadeTo(loopAudio, loopTarget(), ms);
  } else {
    loopAudio.volume = loopTarget();
  }
}

function htmlLoopPlaying() { return loopAudio && !loopAudio.paused; }
function webLoopPlaying() { return loopActive && loopSources.size > 0; }
function anyLoopPlaying() { return loopActive || webLoopPlaying() || htmlLoopPlaying(); }

function base(name) {
  if (!FILES[name]) return null;
  if (!bases[name]) { const a = new Audio(FILES[name]); a.preload = "auto"; bases[name] = a; }
  return bases[name];
}

function clearFade() {
  if (fadeRaf) { cancelAnimationFrame(fadeRaf); fadeRaf = 0; }
  if (fadeTimeout) { clearTimeout(fadeTimeout); fadeTimeout = 0; }
}

function fadeTo(audio, target, ms, onDone) {
  clearFade();
  const from = audio.volume;
  const start = performance.now();
  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    clearFade();
    audio.volume = Math.max(0, Math.min(1, target));
    if (onDone) onDone();
  };
  const tick = (t) => {
    const k = Math.min(1, (t - start) / ms);
    audio.volume = Math.max(0, Math.min(1, from + (target - from) * k));
    if (k < 1) fadeRaf = requestAnimationFrame(tick);
    else finish();
  };
  fadeRaf = requestAnimationFrame(tick);
  fadeTimeout = setTimeout(finish, ms + 150); // garante conclusão mesmo com rAF estrangulado
}

// --- one-shots (click / hover / voice) — dependem só de "silenciar tudo" ---
// Tocados por Web Audio (buffer descodificado) para latência quase-nula; fallback HTMLAudio.
const sfxBuffers = {};
const sfxDecoding = {};

function decodeSfx(name) {
  const ctx = ensureLoopContext();
  if (!ctx || !FILES[name]) return Promise.resolve(null);
  if (sfxBuffers[name]) return Promise.resolve(sfxBuffers[name]);
  if (!sfxDecoding[name]) {
    sfxDecoding[name] = fetch(FILES[name], { cache: "force-cache" })
      .then((res) => { if (!res.ok) throw new Error("sfx"); return res.arrayBuffer(); })
      .then((bytes) => ctx.decodeAudioData(bytes))
      .then((buf) => { sfxBuffers[name] = buf; return buf; })
      .catch(() => { sfxDecoding[name] = null; return null; });
  }
  return sfxDecoding[name];
}

// Pré-descodifica os SFX curtos (decodeAudioData funciona mesmo com o contexto suspenso),
// para que o primeiro clique já saia por Web Audio sem latência.
function warmSfx() { ["click", "hover", "voice"].forEach((n) => decodeSfx(n)); }

function playWeb(name) {
  if (!loopCtx || loopCtx.state !== "running") return false;
  const buf = sfxBuffers[name];
  if (!buf) return false;
  try {
    const src = loopCtx.createBufferSource();
    const gain = loopCtx.createGain();
    gain.gain.value = (VOL[name] ?? 0.5) * volume;
    src.buffer = buf;
    src.connect(gain);
    gain.connect(loopCtx.destination);
    src.onended = () => { try { src.disconnect(); gain.disconnect(); } catch { /* ignore */ } };
    src.start();
    return true;
  } catch { return false; }
}

export function play(name) {
  if (muteAll || !FILES[name]) return;
  if (playWeb(name)) return;          // caminho rápido (Web Audio)
  decodeSfx(name);                    // prepara para os próximos
  const b = base(name);               // fallback: HTMLAudio
  if (!b) return;
  try {
    const node = b.cloneNode(true);
    node.volume = (VOL[name] ?? 0.5) * volume;
    const p = node.play();
    if (p && p.catch) p.catch(() => {});
  } catch { /* ignore */ }
}

function stopIntro() {
  if (introAudio) { try { introAudio.pause(); } catch { /* ignore */ } introAudio = null; }
}

export function startLoop() {
  if (!musicOn()) { phase = "loop"; emit(); return; }

  const ms = loopIntroduced ? LOOP_FADE_IN : LOOP_FADE_IN_FIRST;
  if (!loopIntroduced) {
    loopIntroduced = true;
    try { sessionStorage.setItem(LIKEY, "1"); } catch { /* ignore */ }
  }

  if (loopActive) {
    setWebLoopVolume(loopTarget(), LOOP_FADE_IN);
  } else if (canUseWebAudio()) {
    startWebLoop(ms).catch(() => startHtmlLoop(ms));
  } else {
    startHtmlLoop(ms);
  }

  phase = "loop";
  emit();
}

export function startIntroThenLoop() {
  stopIntro();
  if (!musicOn()) { phase = "loop"; emit(); return; }
  phase = "intro";
  emit();
  primeLoop();
  try {
    introAudio = new Audio(FILES.hero_intro);
    introAudio.volume = VOL.hero_intro * volume;
    const toLoop = () => { if (introAudio) introAudio.removeEventListener("ended", toLoop); startLoop(); };
    introAudio.addEventListener("ended", toLoop);
    const p = introAudio.play();
    if (p && p.catch) p.catch(() => { startLoop(); });
  } catch { startLoop(); }
}

export function enterSite() {
  if (entered) return;
  entered = true;
  try { sessionStorage.setItem(EKEY, "1"); } catch { /* ignore */ }
  primeLoop();
  warmSfx();
  play("click");
  startIntroThenLoop();
  emit();
}

export function stopLoopWithFade() {
  stopIntro();
  stopWebLoop(false);
  if (!loopAudio) return;
  const a = loopAudio;
  loopAudio = null;
  fadeTo(a, 0, LOOP_FADE_OUT, () => { try { a.pause(); a.currentTime = 0; } catch { /* ignore */ } });
}

export function resetToLanding() {
  stopIntro();
  stopLoopWithFade();
  entered = false;
  phase = "landing";
  emit();
}

// --- controlos (bubble de som) ---
export function setVolume(v) {
  volume = Math.max(0, Math.min(1, v));
  persist(VKEY, String(volume));
  if (loopActive && musicOn()) setWebLoopVolume(loopTarget(), 80);
  if (loopAudio && !loopAudio.paused && musicOn()) { clearFade(); loopAudio.volume = loopTarget(); }
  emit();
}

export function setMuteAll(v) {
  muteAll = Boolean(v);
  persist(MKEY, muteAll ? "1" : "0");
  if (muteAll) {
    clearFade();
    stopWebLoop(true);
    if (introAudio) { try { introAudio.pause(); } catch { /* ignore */ } }
    if (loopAudio) { try { loopAudio.pause(); } catch { /* ignore */ } }
  } else if (entered && !muteMusic) {
    startLoop();
    return;
  }
  emit();
}

export function setMuteMusic(v) {
  muteMusic = Boolean(v);
  persist(MMKEY, muteMusic ? "1" : "0");
  if (muteMusic) {
    stopLoopWithFade();
  } else if (!muteAll && entered) {
    startLoop();
    return;
  }
  emit();
}

export function toggleMuteAll() { setMuteAll(!muteAll); }
export function hasEntered() { return entered; }

function subscribe(fn) { subs.add(fn); return () => subs.delete(fn); }
function getSnapshot() { return snapshot; }
export function useAudio() { return useSyncExternalStore(subscribe, getSnapshot, getSnapshot); }

// Reload na mesma sessão (já entrou): sem gate, mas o autoplay exige gesto.
// O primeiro gesto do utilizador retoma o loop.
if (typeof window !== "undefined") {
  const unlock = () => {
    // Aquece o contexto + buffers de SFX no primeiro gesto (mesmo em mudo/sem entrar),
    // para que os cliques saiam por Web Audio sem latência.
    try { const c = ensureLoopContext(); if (c && c.state !== "running") { const p = c.resume(); if (p && p.catch) p.catch(() => {}); } } catch { /* ignore */ }
    warmSfx();
    if (!entered || !musicOn()) return;
    if (phase === "intro" && introAudio && !introAudio.paused) return;
    if (!anyLoopPlaying()) startLoop();
  };
  window.addEventListener("pointerdown", unlock, { passive: true });
  window.addEventListener("keydown", unlock);
}
